import { useState } from 'react'

import classNames from 'classnames'

import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'

import styles from './StatsCarousel.module.css'

interface Stat {
  stat: string
  text: string
  image?: ImageProps
  points?: string[]
}

interface StatsCarouselProps {
  stats: Stat[]
}

const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
  >
    <path
      d="M8 5.18452L9.46328 3.42859L24.2311 15.7351L9.46328 28.0417L8 26.2857L20.6596 15.734L8 5.18452Z"
      fill="white"
    />
  </svg>
)

const StatsCarousel = ({ stats }: StatsCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0)

  const handleNext = () => {
    setActiveIndex(prev => (prev + 1) % stats.length)
  }

  const handlePrev = () => {
    setActiveIndex(prev => (prev - 1 + stats.length) % stats.length)
  }

  return (
    <div className={styles.statsCarousel}>
      <div className={styles.statsWrapper}>
        {/* Height reference element - invisible but maintains layout */}
        {stats.length > 0 && (
          <div className={classNames(styles.statItem, styles.heightReference)}>
            <div className={styles.statContent}>
              <Text
                Tag="div"
                className={styles.statNumber}
                size={TextSizes.primaryTitleLrg}
              >
                {stats[activeIndex].stat}
              </Text>
              <Text
                Tag="p"
                className={styles.statText}
                size={TextSizes.bodyMed}
              >
                {stats[activeIndex].text}
              </Text>
              {stats[activeIndex].points &&
                stats[activeIndex].points.length > 0 && (
                  <ul className={styles.pointsList}>
                    {stats[activeIndex].points.map(
                      (point, idx) =>
                        point && (
                          <li key={idx} className={styles.pointItem}>
                            <Text Tag="span" size={TextSizes.bodySml}>
                              {point}
                            </Text>
                          </li>
                        )
                    )}
                  </ul>
                )}
            </div>
            {stats[activeIndex].image && (
              <div className={styles.statImage}>
                <Image
                  asset={stats[activeIndex].image.asset}
                  alt=""
                  objectFit="contain"
                />
              </div>
            )}
          </div>
        )}

        {/* Actual visible carousel items */}
        {stats.map((stat, index) => (
          <div
            key={index}
            className={classNames(
              styles.statItem,
              activeIndex === index && styles.statItemActive
            )}
          >
            <div className={styles.statContent}>
              <Text
                Tag="div"
                className={styles.statNumber}
                size={TextSizes.primaryTitleLrg}
              >
                {stat.stat}
              </Text>
              <Text
                Tag="p"
                className={styles.statText}
                size={TextSizes.bodyMed}
              >
                {stat.text}
              </Text>
              {stat.points && stat.points.length > 0 && (
                <ul className={styles.pointsList}>
                  {stat.points.map(
                    (point, idx) =>
                      point && (
                        <li key={idx} className={styles.pointItem}>
                          <Text Tag="span" size={TextSizes.bodySml}>
                            {point}
                          </Text>
                        </li>
                      )
                  )}
                </ul>
              )}
            </div>
            {stat.image && (
              <div className={styles.statImage}>
                <Image asset={stat.image.asset} alt="" objectFit="contain" />
              </div>
            )}
          </div>
        ))}
      </div>

      {stats.length > 1 && (
        <div className={styles.navigation}>
          <button
            onClick={handlePrev}
            className={styles.navButton}
            aria-label="Previous stat"
          >
            <div className={styles.prevArrow}>
              <ArrowIcon />
            </div>
          </button>
          <button
            onClick={handleNext}
            className={styles.navButton}
            aria-label="Next stat"
          >
            <ArrowIcon />
          </button>
        </div>
      )}
    </div>
  )
}

export default StatsCarousel
